import React from 'react';

import { useParams } from 'react-router-dom';

import { MenuItem } from '@mui/material';

import Record from 'ui/Record/Record';

import { DefaultFeedMapping } from 'ui/Feed/Feed';

import { CenteredCircularProgress } from 'components/Progress';

import { formatMoment } from 'utils/formatters/timezone';

import DataError from 'containers/Error/DataError';

import { isNotFound } from 'utils/api/errors';

import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';

import EntityRelatedActionsCell from 'components/SharedFormComponents/EntityRelatedActionsCell';

import EntityDetailsContent from 'components/Entities/EntityDetailsContent';
import EntityDetailsActivities from 'components/Entities/EntityDetailsActivities';

import EntityDetailsActivitiesFooter from 'components/Entities/EntityDetailsActivities/EntityDetailsActivitiesFooter';
import EntityDetailsHeader from 'components/Entities/EntityDetailsHeader';
import UserRow from 'components/UserRow';
import { ActivityType } from 'utils/stores/Activities/constants';

import { ActivityMapping, UuidableName } from 'utils/api/types';

import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import EntitiesDetailsLoader from 'ui/DetailsLoader/EntitiesDetailsLoader';

import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, UserRole } from 'core/constants';

import SiteUnitValue from './components/unit/SiteUnitValue';
import SiteUnitModal from './components/unit/SiteUnitModal';

import SiteStateActivity from './components/SiteStateActivity';
import SiteManagerChangeActivity from './components/SiteManagerChangeActivity';
import SiteNameChangeActivity from './components/SiteNameChangeActivity';

const activityTypeToComponent = {
  ...DefaultFeedMapping,
  [ActivityType.SiteState]: SiteStateActivity,
  [ActivityType.SiteManagerChange]: SiteManagerChangeActivity,
  [ActivityType.SiteNameChange]: SiteNameChangeActivity,
} as unknown as ActivityMapping;

const SiteDetails: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const params = useParams<{ entityId: string }>();

  const { siteDetails, session } = useStore();
  const [changingUnit, setChangingUnit] = React.useState(false);

  return (
    <EntitiesDetailsLoader params={params} routeParam="entityId" store={siteDetails}>
      {({ isLoaded, error, reload }): React.ReactElement => {
        if (!isLoaded) {
          return <CenteredCircularProgress />;
        }
        if (error) {
          if (isNotFound(error)) {
            return <NotFound />;
          }
          return <DataError onReload={reload} />;
        }

        if (siteDetails.details === undefined) {
          return <NotFound />;
        }

        const {
          prefix,
          details: { entity },
        } = siteDetails;
        const siteName = entity[`${prefix}name`];
        const siteManager = entity[`${prefix}manager`];

        return (
          <>
            <Record
              navBarHeight={navBarHeight}
              Header={
                <EntityDetailsHeader
                  store={siteDetails}
                  title={siteName.value as string}
                  menuItems={
                    session.user?.scopedPermissions && session.user?.role === UserRole.AccountManager
                      ? (close) => (
                          <MenuItem
                            id="change-unit-button"
                            onClick={() => {
                              close();
                              setChangingUnit(true);
                            }}
                          >
                            {siteDetails.details?.scopeUnit ? 'Change unit' : 'Set unit'}
                          </MenuItem>
                        )
                      : undefined
                  }
                  columns={[
                    { name: 'Name', value: <p>{siteName.value as string}</p> },
                    ...(session.user?.scopedPermissions
                      ? [
                          {
                            name: 'Unit',
                            value: <SiteUnitValue unitUuid={siteDetails.details.scopeUnit ?? null} />,
                          },
                        ]
                      : []),
                    {
                      name: 'Manager',
                      value: (
                        <>
                          {siteManager && siteManager.value ? (
                            <UserRow user={siteManager.value as unknown as UuidableName} />
                          ) : (
                            'N/A'
                          )}
                        </>
                      ),
                    },
                    {
                      name: 'Created',
                      value: (
                        <p data-testid="entity-header-created-at">
                          {formatMoment(siteDetails.details.createdAt, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}
                        </p>
                      ),
                    },
                    {
                      name: 'Related Actions',
                      value: (
                        <EntityRelatedActionsCell
                          relatedActions={siteDetails.details.relatedActions || { total: 0, completed: 0 }}
                        />
                      ),
                    },
                  ]}
                />
              }
              Content={(onError) => <EntityDetailsContent onError={onError} store={siteDetails} />}
              Activities={{
                Content: (
                  <EntityDetailsActivities
                    activities={siteDetails.activities}
                    activityTypeToComponent={activityTypeToComponent}
                  />
                ),
                Footer: <EntityDetailsActivitiesFooter activities={siteDetails.activities} />,
              }}
            />
            {changingUnit && (
              <SiteUnitModal
                siteUuid={siteDetails.details.uuid}
                siteName={siteName.value as string}
                currentUnitUuid={siteDetails.details.scopeUnit ?? null}
                onChanged={reload}
                onClose={() => setChangingUnit(false)}
              />
            )}
          </>
        );
      }}
    </EntitiesDetailsLoader>
  );
};

export default SiteDetails;
